import * as React from 'react';

import { useEventListener } from 'shared/entities/common/hooks';
import { PopupPosition } from 'shared/entities/components/Popup';

/** верхняя граница высоты контента вне зависимости от свободного места */
export const POPUP_MAX_HEIGHT_RATIO = 0.8;

/** запас до края окна, чтобы контент не прилипал к нему вплотную */
const VIEWPORT_MARGIN = 8;

type Params = {
  /** считать высоту только пока ограничение нужно и попап открыт */
  active: boolean;
  triggerRef: React.RefObject<HTMLElement>;
  position: PopupPosition | PopupPosition[];
  gap: number;
  reposition: () => void;
};

const getAvailableHeight = (
  trigger: DOMRect,
  side: string,
  gap: number
): number => {
  const offset = gap + VIEWPORT_MARGIN;

  switch (side) {
    case 'top':
      return trigger.top - offset;
    case 'bottom':
      return window.innerHeight - trigger.bottom - offset;
    // по бокам контент выравнивается вдоль триггера, а не за ним,
    // поэтому по вертикали доступно всё окно
    default:
      return window.innerHeight - VIEWPORT_MARGIN * 2;
  }
};

/**
 * Считает max-height контента по свободному месту между триггером и краем окна.
 *
 * Без этого высокий контент вылезает за пределы окна, а calculatePosition,
 * не найдя позиции с нулевым переполнением, перебирает все POSITION_TYPES
 * и уводит попап на случайную сторону от триггера.
 */
export const usePopupViewportHeight = ({
  active,
  triggerRef,
  position,
  gap,
  reposition
}: Params): number | undefined => {
  const [maxHeight, setMaxHeight] = React.useState<number>();

  const [side] = (Array.isArray(position) ? position[0] : position).split(' ');

  const measure = React.useCallback(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();

    if (!trigger) {
      return;
    }

    setMaxHeight(
      Math.max(
        0,
        Math.min(
          getAvailableHeight(trigger, side, gap),
          window.innerHeight * POPUP_MAX_HEIGHT_RATIO
        )
      )
    );
  }, [triggerRef, side, gap]);

  React.useLayoutEffect(() => {
    if (!active) {
      return;
    }

    measure();
  }, [active, measure]);

  // контент пересобирается под новую высоту, поэтому координаты нужно пересчитать
  React.useLayoutEffect(() => {
    if (!active || maxHeight === undefined) {
      return;
    }

    reposition();
  }, [active, maxHeight, reposition]);

  useEventListener({
    eventName: 'resize',
    handler: () => {
      if (active) {
        measure();
      }
    }
  });

  return active ? maxHeight : undefined;
};
